/**
 * Shared Legislator Types and Interfaces
 * 
 * This file contains all shared types used by:
 * - LegislatorsPage (main directory)
 * - LegislatorsTab (workspace context)
 * - LegislatorsReport (report context)
 */

import { LucideIcon } from 'lucide-react';

// =============================================================================
// Base Legislator Types
// =============================================================================

/**
 * Base legislator data - common fields across all contexts
 * Primary format is camelCase (normalized by useLegislatorData hook)
 */
export interface BaseLegislator {
  id: number;
  
  // Primary camelCase fields (normalized)
  fullName?: string;
  firstName?: string;
  lastName?: string;
  partyAffiliation?: string;
  sessionYear?: string | number;
  sessionKey?: string;
  workPhone?: string;
  sponsoredBillsCount?: number;
  committeeCount?: number;
  
  // Fields that don't need case conversion
  chamber: string;
  district: string;
  email?: string;
  website?: string;
  status?: string;
  committees?: string;
  photoUrl?: string | null;
  
  // Legacy snake_case aliases (for backward compatibility)
  legislator_id?: number;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  party_affiliation?: string;
  session_year?: string | number;
  phone?: string;
  work_phone?: string;
  committee_count?: number;
  sponsored_bills_count?: number;
}

/**
 * Extended legislator with workspace-specific tracking data
 * Primary format is camelCase (normalized by useLegislatorData hook)
 */
export interface TrackedLegislator extends BaseLegislator {
  // Primary camelCase workspace tracking fields (normalized)
  isPriority?: boolean;
  position?: 'Support' | 'Oppose' | 'Monitor' | 'Neutral';
  trackingNotes?: string;
  /** 'Elect' / 'Continuing' for an incoming member (ELECTION_OUTCOMES_PRD §5.14); `status` is the tracking state here. */
  rosterStatus?: string | null;
  /** The day an incoming member takes office (their session's convene date). */
  takesOffice?: string | null;
  
  // Legacy snake_case aliases (for backward compatibility)
  is_priority?: boolean;
  tracking_notes?: string;
}

/**
 * Normalize a legislator object to camelCase format
 * This is a utility function - the primary normalization happens in useLegislatorData hook
 */
export function normalizeLegislator<T extends BaseLegislator>(legislator: T): T {
  return {
    ...legislator,
    id: legislator.id || legislator.legislator_id || 0,
    fullName: legislator.fullName || legislator.full_name || '',
    firstName: legislator.firstName || legislator.first_name || '',
    lastName: legislator.lastName || legislator.last_name || '',
    partyAffiliation: legislator.partyAffiliation || legislator.party_affiliation || '',
    sessionYear: legislator.sessionYear || legislator.session_year || '',
    workPhone: legislator.workPhone || legislator.work_phone || legislator.phone || '',
    sponsoredBillsCount: legislator.sponsoredBillsCount ?? (legislator as any).sponsored_bills_count ?? 0,
  };
}

/**
 * Helper to get session year from a legislator
 */
export function getLegislatorSessionYear(legislator: BaseLegislator): string {
  return String(legislator.sessionYear || legislator.session_year || '');
}

// =============================================================================
// Filter Types
// =============================================================================

/**
 * Available filter values from the API
 */
export interface LegislatorFilterOptions {
  chambers: string[];
  parties: string[];
  districts: District[];
  committees: Committee[];
  sessionYears?: string[];
  currentSessionYear?: string | null;
}

export interface Committee {
  name: string;
  chamber: string;
  displayName: string;
}

export interface District {
  district: string;
  chamber: string;
  displayName: string;
}

/**
 * The sentinel meaning "whatever session is current", resolved server-side.
 * Re-exported so existing importers of this module keep working.
 * See hooks/useSessions.ts.
 */
export { CURRENT_SESSION } from '../../hooks/useSessions';

/*
 * SESSION_YEAR_OPTIONS is gone. It was a literal ['All','2026','2025'], so the
 * Legislators filter could only ever reach two sessions — while the backend has
 * always returned the real DISTINCT list this UI ignored (assessment §5).
 * Options now come from useSessionOptions() in hooks/useSessions.ts.
 */

/**
 * Current filter state
 */
export interface LegislatorFilterState {
  searchQuery: string;
  sessionYear: string;
  chamber: string;
  committee: string;
  party: string;
  district: string;
}

/**
 * Default filter values
 */
export const DEFAULT_FILTER_STATE: LegislatorFilterState = {
  searchQuery: '',
  sessionYear: 'current', // Resolved server-side; never a wall-clock year
  chamber: 'All',
  committee: 'All committees',
  party: 'All parties',
  district: 'All districts',
};

/**
 * Configuration for which filters to show
 */
export interface LegislatorFiltersConfig {
  showSearch?: boolean;
  showSessionYear?: boolean;
  showChamber?: boolean;
  showCommittee?: boolean;
  showParty?: boolean;
  showDistrict?: boolean;
  showClearButton?: boolean;
  showSearchButton?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
}

export const DEFAULT_FILTERS_CONFIG: LegislatorFiltersConfig = {
  showSearch: true,
  showSessionYear: true,
  showChamber: true,
  showCommittee: true,
  showParty: true,
  showDistrict: true,
  showClearButton: true,
  showSearchButton: true,
  compact: false,
};

// =============================================================================
// Sorting Types
// =============================================================================

export type SortField = 'name' | 'district' | 'party' | 'chamber' | 'sponsored_bills_count';
export type SortDirection = 'asc' | 'desc';

export interface SortState {
  field: SortField;
  direction: SortDirection;
}

export const DEFAULT_SORT_STATE: SortState = {
  field: 'name',
  direction: 'asc',
};

// =============================================================================
// Table Configuration Types
// =============================================================================

/**
 * Column definition for configurable table
 */
export interface LegislatorColumnDef<T extends BaseLegislator = BaseLegislator> {
  id: string;
  header: string;
  sortKey?: SortField;
  /** Responsive hide at breakpoint */
  responsiveHide?: 'sm' | 'md' | 'lg' | 'xl';
  /** Column sizing */
  columnType?: 'auto' | 'fit' | 'expand' | 'actions';
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
  /** Custom render function */
  render: (legislator: T, context: LegislatorRenderContext) => React.ReactNode;
}

/**
 * Context passed to column render functions
 */
export interface LegislatorRenderContext {
  workspaceId?: string;
  isPublicView?: boolean;
  /** Force links to public pages (e.g., for reports that should link to public legislator views) */
  usePublicLinks?: boolean;
  permissions?: {
    canView?: boolean;
    canEdit?: boolean;
    canManage?: boolean;
  };
  /** Callback handlers */
  onNavigate?: (legislatorId: number) => void;
  onBookmark?: (legislatorId: number, name: string) => void;
  onPositionChange?: (legislatorId: number, position: string) => void;
  onPriorityToggle?: (legislatorId: number, isPriority: boolean) => void;
  onRemove?: (legislator: TrackedLegislator) => void;
  /** Additional state */
  trackedLegislatorIds?: Set<number>;
  selectedLegislatorIds?: Set<number>;
  onSelectLegislator?: (legislatorId: number, checked: boolean) => void;
}

// =============================================================================
// Card Configuration Types
// =============================================================================

/**
 * Card action button definition
 */
export interface LegislatorCardAction {
  id: string;
  label: string;
  icon: LucideIcon;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger';
  /** Show filled icon when condition is true */
  isActive?: (legislator: BaseLegislator, context: LegislatorRenderContext) => boolean;
  /** Action handler */
  onClick: (legislator: BaseLegislator, context: LegislatorRenderContext) => void;
  /** Hide action based on condition */
  hidden?: (legislator: BaseLegislator, context: LegislatorRenderContext) => boolean;
}

/**
 * Card configuration
 */
export interface LegislatorCardConfig {
  /** Show contact information */
  showContact?: boolean;
  /** Show committees */
  showCommittees?: boolean;
  /** Show sponsored bills count with link */
  showSponsoredBills?: boolean;
  /** Show voting records link */
  showVotingRecords?: boolean;
  /** Show position badge */
  showPosition?: boolean;
  /** Show priority star */
  showPriority?: boolean;
  /** Custom actions */
  actions?: LegislatorCardAction[];
}

// =============================================================================
// Data Source Types
// =============================================================================

/**
 * Data source configuration for useLegislatorData hook
 */
export type LegislatorDataSource = 
  | 'all'           // All legislators (main directory)
  | 'tracked'       // Workspace tracked legislators
  | 'report'        // Workspace report (tracked only)
  | 'report-all';   // Workspace report (all legislators)

export interface LegislatorDataConfig {
  source: LegislatorDataSource;
  workspaceId?: string;
  /** Enable client-side filtering (for smaller datasets) */
  clientSideFiltering?: boolean;
  /** Enable server-side filtering (for larger datasets) */
  serverSideFiltering?: boolean;
}

// =============================================================================
// View Mode Types
// =============================================================================

export type ViewMode = 'table' | 'card';

export interface ViewState {
  mode: ViewMode;
  /** For report: my legislators vs all */
  source?: 'my' | 'all';
}

// =============================================================================
// Export Types
// =============================================================================

export type ExportFormat = 'csv' | 'excel' | 'pdf' | 'word';

// =============================================================================
// Utility Types
// =============================================================================

/**
 * Pagination response from API
 */
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Standard API response for legislators
 */
export interface LegislatorsResponse {
  legislators: BaseLegislator[];
  pagination: PaginationInfo;
}
